import _ from 'lodash';
import type { FormulaNode, IterableFormula, RawFormulaData, StructIterator } from '../typeDefs';

export const isBlock = ($op: string): boolean =>
    $op === 'and' || $op === 'or' || $op === 'checker-list';

const isEmpty = (val: unknown): boolean =>
    val == null || val === '' || (typeof val === 'string' && val.trim() === '');

export const concatPath = (
    a: string | null | undefined,
    b: string | null | undefined,
): string => {
    const aEmpty = isEmpty(a);
    const bEmpty = isEmpty(b);

    return !aEmpty && !bEmpty ? a + '.' + b : aEmpty ? b || '' : a || '';
};

// Iterates over every checker-message (and $checker prop) of a structure
export const messageIterate = (
    checkerStructure: FormulaNode | null | undefined,
    iterator: (node: FormulaNode, path: string) => void,
): void => {
    const exec = (obj: FormulaNode, path: string) => {
        if (typeof obj !== 'object' || obj.$op !== 'checker-message') return;
        iterator(obj, path);
    };

    structIterate(checkerStructure, (obj, path) => {
        if (obj.$op === 'checker-message') exec(obj, path);
        else if (obj.$checker) _.each(obj.$checker, (child: FormulaNode, key: string) => exec(child, concatPath(path, '$checker.' + key)));
    });
};

export const getMaxMessagePriority = (checkerStructure: FormulaNode | null | undefined): number => {
    let maxPriority = -1;

    messageIterate(checkerStructure, obj => {
        maxPriority = Math.max(maxPriority, obj.$priority || -1);
    });

    return maxPriority;
};

// Iterates over each relevant expression of a formula (formula.structure + formula.functions)
export const formulaIterate = (formula: IterableFormula, iterator: StructIterator): void => {
    const base: Array<Array<string | number>> = [['structure'], ['checker'], ['filter']];
    const allStructs = base.concat(
        _.map(formula.functions, (_item, index) => ['functions', index, 'structure']),
    );

    for (let path of allStructs) {
        const structure = _.get(formula, path);
        if (!structure) continue;
        structIterate(structure, iterator, path);
    }
};

export const structIterate = (
    structure: FormulaNode | null | undefined,
    fn: StructIterator,
    path: Array<string | number> = [],
    parent: FormulaNode | null = null,
): void => {
    if (structure == null) return;
    if (typeof structure !== 'object') return;

    const next = (obj: FormulaNode | null | undefined, key: Array<string | number>) =>
        structIterate(obj, fn, path.concat(key), structure);

    fn(structure, path.join('.'), parent);

    switch (structure.$op) {
        case 'if':
            next(structure.$value, ['$value']);
            next(structure.$then, ['$then']);
            next(structure.$else, ['$else']);
            break;
        case 'for':
            next(structure.$value, ['$value']);
            break;
        case 'and':
        case 'or':
        case 'checker-list':
            structure.$value.forEach((item: FormulaNode, index: number) => next(item, ['$value', index]));
            break;
        case 'checker-table':
            structure.$value.forEach((item: FormulaNode, index: number) => next(item, ['$value', index]));
            structure.$args?.forEach((item: FormulaNode, index: number) => next(item, ['$args', index]));
            break;
        case 'schema':
            // This was a hack before. Ideally this should remain disabled
            // structure.$args.forEach((item, index) => next(item, ['$args', index]));
            break;
        case 'ref':
        case 'fn':
        case 'call':
        case 'checker-message':
        case 'checker-var':
            break;
        default:
            console.warn(structure);
            throw new Error(`Unknown operation: '${structure.$op}'`);
    }
};

// Removes all the redux-specific annotations from the formula
export const cleanFormula = (formula: RawFormulaData): RawFormulaData => {
    const clone: RawFormulaData = JSON.parse(JSON.stringify(formula));

    const allStructs = [
        clone.structure,
        clone.checker,
        ..._.map(clone.functions, 'structure'),
    ].filter((x): x is FormulaNode => !!x);

    const clean = (node: FormulaNode) => {
        const obj = node as unknown as Record<string, unknown>;
        for (let key in obj) {
            if (key[0] === '$') continue;
            delete obj[key];
        }

        // legacy prop
        const checker = node.$checker as Record<string, unknown> | undefined;
        if (checker) {
            delete checker.template;
            for (let key in checker) {
                if (typeof checker[key] === 'string') continue;
                clean(checker[key] as FormulaNode);
            }
        }

        if (!_.size(node.$checker)) delete node.$checker;
    };

    for (let struct of allStructs) structIterate(struct, clean);

    const extra = clone as { options?: unknown; presets?: unknown };
    if (extra.options) delete extra.options;
    if (extra.presets) delete extra.presets;

    return clone;
};
